import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Contact, Link2, Search } from "lucide-react";

const STATUSES = ["employed", "casual", "agency", "contractor", "volunteer"];
const empty = { first_name: "", last_name: "", email: "", phone: "", job_title: "", employment_status: "employed", primary_department: "Other", site_ids: [], start_date: "", leaving_date: "", notes: "", user_id: "" };

export default function People() {
  const { has } = useAuth();
  const [people, setPeople] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [fActive, setFActive] = useState("all");
  const [fDept, setFDept] = useState("all");
  const [fLinked, setFLinked] = useState("all");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const canManage = has("manage_people");

  const load = async () => {
    try {
      const params = {};
      if (fActive !== "all") params.active = fActive;
      if (fDept !== "all") params.department = fDept;
      if (fLinked !== "all") params.linked = fLinked;
      if (q) params.q = q;
      const { data } = await api.get("/people", { params });
      setPeople(data);
    } catch { toast.error("Could not load people"); }
  };
  useEffect(() => {
    api.get("/people/departments").then((r) => setDepartments(r.data)).catch(() => {});
    api.get("/me/sites").then((r) => setSites(r.data.sites || [])).catch(() => {});
    api.get("/staff").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [fActive, fDept, fLinked, q]);

  const openNew = () => { setForm(empty); setEditId(null); setModal(true); };
  const openEdit = (p) => {
    setForm({ first_name: p.first_name || "", last_name: p.last_name || "", email: p.email || "", phone: p.phone || "",
      job_title: p.job_title || "", employment_status: p.employment_status || "employed",
      primary_department: p.primary_department || p.department || "Other", site_ids: p.site_ids || [],
      start_date: p.start_date || "", leaving_date: p.leaving_date || "", notes: p.notes || "", user_id: p.user_id || "" });
    setEditId(p.person_id); setModal(true);
  };
  const toggleSite = (sid) => setForm((f) => ({ ...f, site_ids: f.site_ids.includes(sid) ? f.site_ids.filter((s) => s !== sid) : [...f.site_ids, sid] }));

  const save = async () => {
    try {
      const payload = { ...form, departments: [form.primary_department] };
      if (editId) { await api.put(`/people/${editId}`, payload); toast.success("Person updated"); }
      else { await api.post("/people", payload); toast.success("Person added"); }
      setModal(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const setActive = async (p, active) => {
    try { await api.post(`/people/${p.person_id}/deactivate`, { active }); toast.success(active ? "Reactivated" : "Deactivated"); load(); }
    catch { toast.error("Could not update"); }
  };
  const unlinkedUsers = users.filter((u) => u.role !== "superadmin");

  return (
    <div>
      <PageHeader title="People" subtitle="Your whole team — kitchen and front of house — with or without a login." testid="people-header"
        action={canManage && <Button className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="add-person-btn" onClick={openNew}><Plus size={18} className="mr-1" /> Add Person</Button>} />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <Input data-testid="people-search" placeholder="Search name, email, role…" className="pl-9 rounded-xl" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={fActive} onValueChange={setFActive}><SelectTrigger data-testid="filter-active" className="w-[130px] rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="true">Active</SelectItem><SelectItem value="false">Inactive</SelectItem></SelectContent></Select>
        <Select value={fDept} onValueChange={setFDept}><SelectTrigger data-testid="filter-dept" className="w-[160px] rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All departments</SelectItem>{departments.map((d) => <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>)}</SelectContent></Select>
        <Select value={fLinked} onValueChange={setFLinked}><SelectTrigger data-testid="filter-linked" className="w-[150px] rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Login: any</SelectItem><SelectItem value="true">Has login</SelectItem><SelectItem value="false">No login</SelectItem></SelectContent></Select>
      </div>

      {people.length === 0 ? <EmptyState icon={Contact} title="No people yet" message="Add your team members — they don't need a login." /> : (
        <div className="cc-card divide-y" data-testid="people-list">
          {people.map((p) => (
            <div key={p.person_id} className="flex items-center gap-3 p-3.5 hover:bg-slate-50 cursor-pointer" data-testid={`person-row-${p.person_id}`} onClick={() => canManage && openEdit(p)}>
              <div className="w-9 h-9 rounded-full bg-brand/10 text-brand flex items-center justify-center font-semibold text-sm">{(p.display_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{p.display_name} {!p.active && <span className="text-xs text-slate-400">(inactive)</span>}</div>
                <div className="text-xs text-slate-500 truncate">{p.job_title || "—"} · {p.primary_department || p.department || "Other"}</div>
              </div>
              {p.user_id ? <Badge variant="outline" className="gap-1 text-xs"><Link2 size={11} /> Login</Badge> : <Badge variant="secondary" className="text-xs">No login</Badge>}
              {canManage && <Button size="sm" variant="ghost" className="text-xs" data-testid={`toggle-active-${p.person_id}`} onClick={(e) => { e.stopPropagation(); setActive(p, !p.active); }}>{p.active ? "Deactivate" : "Reactivate"}</Button>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-lg" data-testid="person-modal">
          <DialogHeader><DialogTitle>{editId ? "Edit person" : "Add person"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label>First name</Label><Input data-testid="person-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div><Label>Last name</Label><Input data-testid="person-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Job title</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
            <div><Label>Employment</Label>
              <Select value={form.employment_status} onValueChange={(v) => setForm({ ...form, employment_status: v })}><SelectTrigger data-testid="person-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Department</Label>
              <Select value={form.primary_department} onValueChange={(v) => setForm({ ...form, primary_department: v })}><SelectTrigger data-testid="person-dept"><SelectValue /></SelectTrigger>
                <SelectContent>{departments.map((d) => <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Sites</Label>
              <div className="flex flex-wrap gap-3 mt-1">{sites.filter((s) => s.venue_id).map((s) => (
                <label key={s.venue_id} className="flex items-center gap-2 text-sm"><Checkbox checked={form.site_ids.includes(s.venue_id)} onCheckedChange={() => toggleSite(s.venue_id)} data-testid={`person-site-${s.venue_id}`} /> {s.name}</label>))}</div></div>
            <div className="col-span-2"><Label>Link login (optional)</Label>
              <Select value={form.user_id || "none"} onValueChange={(v) => setForm({ ...form, user_id: v === "none" ? "" : v })}><SelectTrigger data-testid="person-link"><SelectValue placeholder="No login" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No login</SelectItem>{unlinkedUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>)}</SelectContent></Select></div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModal(false)}>Cancel</Button><Button className="bg-brand hover:bg-brand-hover" data-testid="save-person-btn" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
